import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import CampaignService from '../../../services/CampaignService';
import SitesService from '../../../services/SitesService';
import { getLogo } from '../../../services/LogoStorageService';
import { formatMoney } from '../../../utils/currency';

/** Recorta la imagen a círculo (centro) y devuelve un File PNG */
const cropImageToCircle = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const size = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, (img.width - size) / 2 * -1, (img.height - size) / 2 * -1, img.width, img.height);
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('No se pudo generar la imagen')); return; }
        resolve(new File([blob], 'stamp-completed.png', { type: 'image/png' }));
      }, 'image/png', 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Error al cargar la imagen')); };
    img.src = url;
  });
};

const loyaltyTicketDefaults = {
  ticketTitle: 'Cartilla de sellos',
  ticketEarnedText: 'Sumaste 1 sello con esta compra',
  ticketCompletedText: '¡Completaste tu cartilla!',
  ticketFooter: '¡Gracias por tu compra!'
};

const pedidosYaTicketDefaults = {
  pedidosYaTicketTitle: 'Sumá sellos en tu próxima compra',
  pedidosYaTicketDescription: 'En tu próxima compra por nuestra web o en el local podés sumar sellos y acercarte a tu recompensa.',
  pedidosYaTicketEarnedText: 'Comprando desde {monto_minimo} sumás 1 sello.',
  pedidosYaTicketRewardText: 'Completá {sellos} sellos y ganá: {premio}',
  pedidosYaTicketFooter: 'Pedí directo y aprovechá más beneficios.'
};

const getRewardText = (formData) => (
  formData.rewardType === 'percentage'
    ? `${Number(formData.discountPercentage || 0)}% de descuento`
    : formData.rewardType === 'amount'
      ? `${formatMoney(Number(formData.discountAmount || 0))} de descuento`
      : (formData.freeItemDescription || 'Artículo gratis')
);

const PedidosYaIncentiveTicketPreview = ({ formData, logoUrl, companyName = 'Empresa' }) => {
  const purchasesRequired = Math.max(1, Math.min(20, Number(formData.purchasesRequired) || 5));
  const minimumSpend = Number(formData.minimumSpend) || 0;
  const fill = (key) => (formData[key]?.trim() ? formData[key] : pedidosYaTicketDefaults[key])
    .split('{monto_minimo}').join(minimumSpend <= 0 ? 'cualquier monto' : formatMoney(minimumSpend))
    .split('{sellos}').join(String(purchasesRequired))
    .split('{premio}').join(getRewardText(formData));

  return (
    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
      <Box
        sx={{
          width: 250,
          maxWidth: '100%',
          bgcolor: '#fff',
          color: '#111',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          px: 1.5,
          py: 2,
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center'
        }}
      >
        {logoUrl ? (
          <Box component="img" src={logoUrl} alt={companyName} sx={{ display: 'block', maxWidth: 110, maxHeight: 52, objectFit: 'contain', mx: 'auto', mb: 2 }} />
        ) : (
          <Typography sx={{ fontFamily: 'serif', fontSize: 28, lineHeight: 1, mb: 2 }}>{companyName}</Typography>
        )}
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{fill('pedidosYaTicketTitle')}</Typography>
        <Typography sx={{ fontSize: 11 }}>Ticket N°: 28944</Typography>
        <Typography sx={{ fontSize: 11, mb: 1 }}>Fecha: 22/07/2026 18:14</Typography>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace', mb: 1 }}>{'-'.repeat(34)}</Typography>
        <Typography sx={{ fontSize: 12, mb: 1.25 }}>{fill('pedidosYaTicketDescription')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 34px)', justifyContent: 'center', gap: 0.75, my: 1.5 }}>
          {Array.from({ length: purchasesRequired }, (_, index) => (
            <Box key={index} sx={{ width: 34, height: 34, border: '2px solid #cfcfcf', borderRadius: '50%', bgcolor: '#fff' }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>{fill('pedidosYaTicketEarnedText')}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5 }}>{fill('pedidosYaTicketRewardText')}</Typography>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>{'-'.repeat(34)}</Typography>
        <Typography sx={{ fontSize: 12 }}>{fill('pedidosYaTicketFooter')}</Typography>
      </Box>
    </Box>
  );
};

const LoyaltyCardTicketPreview = ({ formData, logoUrl, stampImageUrl, companyName = 'Empresa' }) => {
  const purchasesRequired = Math.max(1, Number(formData.purchasesRequired) || 5);
  const currentPurchases = Math.min(purchasesRequired, Math.max(1, Math.ceil(purchasesRequired * 0.6)));
  const missing = Math.max(0, purchasesRequired - currentPurchases);
  const stampsColor = formData.emailStampsColor || '#4caf50';
  const rewardText = formData.rewardType === 'percentage'
    ? `${Number(formData.discountPercentage || 0)}% de descuento`
    : formData.rewardType === 'amount'
      ? `${formatMoney(Number(formData.discountAmount || 0))} de descuento`
      : (formData.freeItemDescription || 'Artículo gratis');

  return (
    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
      <Box
        sx={{
          width: 250,
          maxWidth: '100%',
          bgcolor: '#fff',
          color: '#111',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          px: 1.5,
          py: 2,
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center'
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={companyName}
            sx={{ display: 'block', maxWidth: 110, maxHeight: 52, objectFit: 'contain', mx: 'auto', mb: 2 }}
          />
        ) : (
          <Typography sx={{ fontFamily: 'serif', fontSize: 28, lineHeight: 1, mb: 2 }}>{companyName}</Typography>
        )}
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{formData.ticketTitle || loyaltyTicketDefaults.ticketTitle}</Typography>
        <Typography sx={{ fontSize: 11 }}>Ticket N°: 28944</Typography>
        <Typography sx={{ fontSize: 11, mb: 1 }}>Fecha: 22/07/2026 18:14</Typography>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace', mb: 1 }}>{'-'.repeat(34)}</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.75 }}>Cliente de ejemplo</Typography>
        <Typography sx={{ fontSize: 13, mb: 1.25 }}>{formData.ticketEarnedText || loyaltyTicketDefaults.ticketEarnedText}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 34px)', justifyContent: 'center', gap: 0.75, my: 1.5 }}>
          {Array.from({ length: purchasesRequired }, (_, index) => (
            <Box
              key={index}
              sx={{
                width: 34,
                height: 34,
                border: '2px solid',
                borderColor: index < currentPurchases ? stampsColor : '#cfcfcf',
                borderRadius: '50%',
                bgcolor: index < currentPurchases ? stampsColor : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 16,
                fontWeight: 800,
                lineHeight: 1
              }}
            >
              {index < currentPurchases && stampImageUrl ? (
                <Box
                  component="img"
                  src={stampImageUrl}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }}
                />
              ) : index < currentPurchases ? '✓' : ''}
            </Box>
          ))}
        </Box>
        {currentPurchases >= purchasesRequired ? (
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.75 }}>{formData.ticketCompletedText || loyaltyTicketDefaults.ticketCompletedText}</Typography>
        ) : (
          <Typography sx={{ fontSize: 12, mb: 0.75 }}>Te faltan {missing} compras</Typography>
        )}
        <Typography sx={{ fontSize: 11, mb: 1.5 }}>Recompensa: {rewardText}</Typography>
        <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>{'-'.repeat(34)}</Typography>
        <Typography sx={{ fontSize: 12 }}>{formData.ticketFooter || loyaltyTicketDefaults.ticketFooter}</Typography>
      </Box>
    </Box>
  );
};

const LoyaltyCardTab = ({ isActive = true }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    enabled: false,
    purchasesRequired: 5,
    minimumSpend: 0,
    freeItemDescription: '',
    discountPercentage: 0,
    discountAmount: 0,
    rewardType: 'free_item', // 'free_item', 'percentage', 'amount'
    printTicket: false,
    sendEmail: true,
    pedidosYaIncentiveEnabled: false,
    salesChannel: 'both',
    ...loyaltyTicketDefaults,
    ...pedidosYaTicketDefaults,
    startDate: null,
    endDate: null,
    emailHeaderColor: '#1976d2',
    emailStampsColor: '#4caf50',
    completedStampImage: '',
    emailBackgroundColor: '#ffffff',
    emailTextColor: '#333333'
  });
  const [uploadingStampImage, setUploadingStampImage] = useState(false);
  const [stampPreviewUrl, setStampPreviewUrl] = useState(''); // URL para mostrar preview del sello (presignada o recién subida)
  const [ticketLogoUrl, setTicketLogoUrl] = useState('');
  const stampImageInputRef = useRef(null);

  // Cargar configuración de campaña tipo cartilla
  const loadCampaignConfig = useCallback(async () => {
    console.log('[LoyaltyCardTab] Loading campaign config - Starting...');
    try {
      setLoading(true);
      setError(null);
      const response = await CampaignService.getLoyaltyCardCampaign();
      console.log('[LoyaltyCardTab] Full response:', response);
      console.log('[LoyaltyCardTab] Response data:', response.data);
      
      // Manejar estructura anidada: response.data.data.data o response.data.data
      const campaignData = response.data?.data?.data || response.data?.data || response.data;
      console.log('[LoyaltyCardTab] Campaign data extracted:', campaignData);
      
      if (campaignData) {
        const start = campaignData.startDate;
        const end = campaignData.endDate;
        setFormData({
          enabled: campaignData.enabled ?? false,
          purchasesRequired: campaignData.purchasesRequired || 5,
          minimumSpend: campaignData.minimumSpend || 0,
          freeItemDescription: campaignData.freeItemDescription || '',
          discountPercentage: campaignData.discountPercentage || 0,
          discountAmount: campaignData.discountAmount || 0,
          rewardType: campaignData.rewardType || 'free_item',
          printTicket: campaignData.printTicket === true,
          sendEmail: campaignData.sendEmail !== false,
          pedidosYaIncentiveEnabled: campaignData.pedidosYaIncentiveEnabled === true,
          salesChannel: campaignData.salesChannel || 'both',
          ticketTitle: campaignData.ticketTitle || loyaltyTicketDefaults.ticketTitle,
          ticketEarnedText: campaignData.ticketEarnedText || loyaltyTicketDefaults.ticketEarnedText,
          ticketCompletedText: campaignData.ticketCompletedText || loyaltyTicketDefaults.ticketCompletedText,
          ticketFooter: campaignData.ticketFooter || loyaltyTicketDefaults.ticketFooter,
          pedidosYaTicketTitle: campaignData.pedidosYaTicketTitle || pedidosYaTicketDefaults.pedidosYaTicketTitle,
          pedidosYaTicketDescription: campaignData.pedidosYaTicketDescription || pedidosYaTicketDefaults.pedidosYaTicketDescription,
          pedidosYaTicketEarnedText: campaignData.pedidosYaTicketEarnedText || pedidosYaTicketDefaults.pedidosYaTicketEarnedText,
          pedidosYaTicketRewardText: campaignData.pedidosYaTicketRewardText || pedidosYaTicketDefaults.pedidosYaTicketRewardText,
          pedidosYaTicketFooter: campaignData.pedidosYaTicketFooter || pedidosYaTicketDefaults.pedidosYaTicketFooter,
          startDate: start ? (typeof start === 'string' ? start.substring(0, 10) : null) : null,
          endDate: end ? (typeof end === 'string' ? end.substring(0, 10) : null) : null,
          emailHeaderColor: campaignData.emailHeaderColor || '#1976d2',
          emailStampsColor: campaignData.emailStampsColor || '#4caf50',
          completedStampImage: campaignData.completedStampImage || '',
          emailBackgroundColor: campaignData.emailBackgroundColor || '#ffffff',
          emailTextColor: campaignData.emailTextColor || '#333333'
        });
        const previewUrl = campaignData.completedStampImageUrl ?? campaignData.completed_stamp_image_url ?? '';
        setStampPreviewUrl(previewUrl);
        if (campaignData.completedStampImage && !previewUrl) {
          const url = await CampaignService.getLoyaltyCardStampImageUrl();
          if (url) setStampPreviewUrl(url);
        }
        console.log('[LoyaltyCardTab] Form data updated successfully');
      }
    } catch (err) {
      console.error('[LoyaltyCardTab] Error loading loyalty card campaign:', err);
      console.error('[LoyaltyCardTab] Error status:', err.response?.status);
      // Si es 404, la campaña no existe aún, usar valores por defecto
      if (err.response?.status !== 404) {
        setError(t('error_loading_loyalty_card_campaign') || 'Error al cargar la campaña tipo cartilla');
      } else {
        console.log('[LoyaltyCardTab] Campaign not found (404) - using defaults');
      }
    } finally {
      setLoading(false);
      console.log('[LoyaltyCardTab] Loading campaign config - Completed');
    }
  }, [t]);

  // Cargar (o recargar) al montar y cada vez que se entra a la pestaña, para tener siempre URL de preview fresca del sello
  useEffect(() => {
    if (isActive) loadCampaignConfig();
  }, [isActive, loadCampaignConfig]);

  useEffect(() => {
    if (!isActive) return;
    let mounted = true;
    const loadTicketLogo = async () => {
      try {
        const logo = await getLogo();
        if (mounted) setTicketLogoUrl(logo || '');
      } catch (err) {
        if (mounted) setTicketLogoUrl('');
      }
    };
    loadTicketLogo();
    return () => {
      mounted = false;
    };
  }, [isActive]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validaciones solo si la campaña está habilitada
    if (formData.enabled) {
      if (!formData.purchasesRequired || formData.purchasesRequired < 1) {
        setError(t('purchases_required_invalid') || 'El número de compras requeridas debe ser al menos 1');
        return;
      }
      
      if (formData.minimumSpend < 0) {
        setError(t('minimum_spend_amount'));
        return;
      }

      if (formData.rewardType === 'free_item' && (!formData.freeItemDescription || !formData.freeItemDescription.trim())) {
        setError(t('free_item_description_required') || 'La descripción del artículo gratis es obligatoria');
        return;
      }

      // Validar descuento máximo si se ingresó (opcional pero debe ser mayor a 0 si se ingresa)
      if (formData.rewardType === 'free_item' && formData.discountAmount !== undefined && formData.discountAmount !== null && formData.discountAmount < 0) {
        setError(t('invalid_discount_amount') || 'El monto de descuento debe ser mayor o igual a 0');
        return;
      }

      if (formData.rewardType === 'percentage' && (formData.discountPercentage <= 0 || formData.discountPercentage > 100)) {
        setError(t('invalid_discount_percentage') || 'El porcentaje de descuento debe estar entre 1 y 100');
        return;
      }

      if (formData.rewardType === 'amount' && formData.discountAmount <= 0) {
        setError(t('invalid_discount_amount') || 'El monto de descuento debe ser mayor a 0');
        return;
      }

      if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
        setError(t('loyalty_card_invalid_date_range') || 'La fecha de inicio debe ser anterior o igual a la fecha de fin');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      const campaignData = {
        enabled: formData.enabled,
        purchasesRequired: formData.purchasesRequired,
        minimumSpend: formData.minimumSpend,
        freeItemDescription: formData.freeItemDescription || null,
        discountPercentage: formData.discountPercentage || 0,
        discountAmount: formData.discountAmount || 0,
        rewardType: formData.rewardType,
        printTicket: formData.printTicket === true,
        sendEmail: formData.sendEmail !== false,
        pedidosYaIncentiveEnabled: formData.pedidosYaIncentiveEnabled === true,
        salesChannel: formData.salesChannel || 'both',
        ticketTitle: formData.ticketTitle || null,
        ticketEarnedText: formData.ticketEarnedText || null,
        ticketCompletedText: formData.ticketCompletedText || null,
        ticketFooter: formData.ticketFooter || null,
        pedidosYaTicketTitle: formData.pedidosYaTicketTitle || null,
        pedidosYaTicketDescription: formData.pedidosYaTicketDescription || null,
        pedidosYaTicketEarnedText: formData.pedidosYaTicketEarnedText || null,
        pedidosYaTicketRewardText: formData.pedidosYaTicketRewardText || null,
        pedidosYaTicketFooter: formData.pedidosYaTicketFooter || null,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        emailHeaderColor: formData.emailHeaderColor || null,
        emailStampsColor: formData.emailStampsColor || null,
        completedStampImage: formData.completedStampImage || null,
        emailBackgroundColor: formData.emailBackgroundColor || null,
        emailTextColor: formData.emailTextColor || null
      };

      await CampaignService.saveLoyaltyCardCampaign(campaignData);

      SnackbarUtils.success(t('loyalty_card_campaign_saved_successfully') || 'Campaña tipo cartilla guardada exitosamente');
    } catch (err) {
      console.error('Error saving loyalty card campaign:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      setError(errorMessage || (t('error_saving_loyalty_card_campaign') || 'Error al guardar la campaña tipo cartilla'));
      SnackbarUtils.error(errorMessage || (t('error_saving_loyalty_card_campaign') || 'Error al guardar la campaña tipo cartilla'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              {t('loyalty_card_campaign_configuration') || 'Configuración de Campaña Tipo Cartilla'}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
              }
              label={t('enable_loyalty_card_campaign') || 'Habilitar campaña tipo cartilla'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('loyalty_card_campaign_description') || 'Configura el sistema de recompensas por compras acumuladas. Los clientes recibirán una recompensa después de completar un número determinado de compras.'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        {formData.enabled && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Dónde aplica
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={formData.salesChannel || 'both'}
                  onChange={(e) => handleChange('salesChannel', e.target.value)}
                >
                  <FormControlLabel value="both" control={<Radio />} label="Ambos" />
                  <FormControlLabel value="local" control={<Radio />} label="Solo local" />
                  <FormControlLabel value="web" control={<Radio />} label="Solo web" />
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Define si la cartilla suma y se comunica en ventas del local, compras web o ambos canales.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('printed_ticket') || 'Ticket impreso'}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.printTicket === true}
                    onChange={(e) => handleChange('printTicket', e.target.checked)}
                  />
                }
                label={t('loyalty_card_print_ticket') || 'Imprimir ticket de cartilla al finalizar la venta'}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('loyalty_card_print_ticket_helper') || 'Si está activo, las ventas locales elegibles imprimen un ticket adicional con el progreso de sellos. Si la venta no cumple el mínimo, no se imprime nada.'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Envío de correo
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sendEmail !== false}
                    onChange={(e) => handleChange('sendEmail', e.target.checked)}
                  />
                }
                label="Enviar correo de cartilla al finalizar la venta"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Si está activo, las ventas elegibles envían un correo al cliente con el progreso de sellos.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Incentivo PedidoYa
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.pedidosYaIncentiveEnabled === true}
                    onChange={(e) => handleChange('pedidosYaIncentiveEnabled', e.target.checked)}
                  />
                }
                label="Imprimir incentivo para clientes de PedidoYa"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Si está activo, las ventas de PedidoYa que alcancen el mínimo imprimen un mensaje para invitar al cliente a comprar por la web o en el local y empezar a sumar sellos.
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {formData.pedidosYaIncentiveEnabled === true && (
              <Grid item xs={12}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Contenido del ticket de incentivo de PedidosYa
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Textos que se imprimen en el ticket de incentivo. Podés usar {'{monto_minimo}'}, {'{sellos}'} y {'{premio}'}. Si dejás un campo vacío se usa el texto por defecto.
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        ['pedidosYaTicketTitle', 'Título', 'Encabezado del ticket.', false],
                        ['pedidosYaTicketDescription', 'Descripción', 'Mensaje principal, arriba de los sellos.', true],
                        ['pedidosYaTicketEarnedText', 'Texto de sello', 'Línea en negrita debajo de los sellos.', true],
                        ['pedidosYaTicketRewardText', 'Beneficio destacado', 'Texto del premio, en negrita.', true],
                        ['pedidosYaTicketFooter', 'Texto final', 'Cierre del ticket.', false]
                      ].map(([key, label, helper, multiline]) => (
                        <Grid item xs={12} key={key}>
                          <TextField
                            fullWidth
                            multiline={multiline}
                            minRows={multiline ? 2 : undefined}
                            label={label}
                            value={formData[key] || ''}
                            onChange={(e) => handleChange(key, e.target.value)}
                            helperText={helper}
                            inputProps={{ maxLength: key === 'pedidosYaTicketTitle' ? 160 : 255 }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle1" align="center" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Vista previa
                    </Typography>
                    <PedidosYaIncentiveTicketPreview formData={formData} logoUrl={ticketLogoUrl} companyName="Empresa" />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
              </Grid>
            )}

            {formData.printTicket === true && (
              <Grid item xs={12}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Contenido del ticket
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Textos que se imprimen en el ticket de progreso de la cartilla.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Título del ticket"
                          value={formData.ticketTitle || ''}
                          onChange={(e) => handleChange('ticketTitle', e.target.value)}
                          helperText="Encabezado visible debajo del logo."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Texto al sumar sello"
                          value={formData.ticketEarnedText || ''}
                          onChange={(e) => handleChange('ticketEarnedText', e.target.value)}
                          helperText="Se muestra cuando la compra suma progreso."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Texto al completar"
                          value={formData.ticketCompletedText || ''}
                          onChange={(e) => handleChange('ticketCompletedText', e.target.value)}
                          helperText="Se imprime cuando el cliente completa la cartilla."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Texto final"
                          value={formData.ticketFooter || ''}
                          onChange={(e) => handleChange('ticketFooter', e.target.value)}
                          helperText="Cierre del ticket."
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle1" align="center" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Vista previa
                    </Typography>
                    <LoyaltyCardTicketPreview formData={formData} logoUrl={ticketLogoUrl} stampImageUrl={stampPreviewUrl} companyName="Empresa" />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
              </Grid>
            )}

            {/* Sección: Configuración de compras requeridas */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('purchases_configuration') || '1. Configuración de compras'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={6} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('purchases_required') || 'Compras requeridas'}
                value={formData.purchasesRequired}
                onChange={(e) => handleChange('purchasesRequired', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, step: 1 }}
                helperText={t('purchases_required_helper') || 'Número de compras que el cliente debe realizar para obtener la recompensa'}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('minimum_spend')}
                value={formData.minimumSpend}
                onChange={(e) => handleChange('minimumSpend', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, step: 1 }}
                helperText={t('minimum_spend_helper')}
              />
            </Grid>

            {/* Vigencia de la campaña */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                {t('loyalty_card_validity') || 'Vigencia de la campaña'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('loyalty_card_validity_helper') || 'Opcional. Solo se contabilizan compras dentro de este período.'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={6}>
              <TextField
                fullWidth
                type="date"
                label={t('loyalty_card_start_date') || 'Fecha de inicio'}
                value={formData.startDate || ''}
                onChange={(e) => handleChange('startDate', e.target.value || null)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <TextField
                fullWidth
                type="date"
                label={t('loyalty_card_end_date') || 'Fecha de fin'}
                value={formData.endDate || ''}
                onChange={(e) => handleChange('endDate', e.target.value || null)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Colores del email */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                {t('loyalty_card_email_colors') || 'Colores del email'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('loyalty_card_email_colors_helper') || 'Personalizá el encabezado y los puntitos del correo de la cartilla.'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {t('loyalty_card_email_header_color') || 'Color del encabezado'}
                  </Typography>
                  <input
                    type="color"
                    value={formData.emailHeaderColor || '#1976d2'}
                    onChange={(e) => handleChange('emailHeaderColor', e.target.value)}
                    style={{ width: 40, height: 36, padding: 2, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
                  />
                </Box>
                <TextField
                  size="small"
                  value={formData.emailHeaderColor || '#1976d2'}
                  onChange={(e) => handleChange('emailHeaderColor', e.target.value)}
                  placeholder="#1976d2"
                  sx={{ width: 110 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {t('loyalty_card_email_stamps_color') || 'Color de los puntitos'}
                  </Typography>
                  <input
                    type="color"
                    value={formData.emailStampsColor || '#4caf50'}
                    onChange={(e) => handleChange('emailStampsColor', e.target.value)}
                    style={{ width: 40, height: 36, padding: 2, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
                  />
                </Box>
                <TextField
                  size="small"
                  value={formData.emailStampsColor || '#4caf50'}
                  onChange={(e) => handleChange('emailStampsColor', e.target.value)}
                  placeholder="#4caf50"
                  sx={{ width: 110 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('loyalty_card_completed_stamp_image')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('loyalty_card_completed_stamp_image_helper')}
              </Typography>
              <input
                ref={stampImageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target?.files?.[0];
                  if (!file || !file.type.startsWith('image/')) {
                    SnackbarUtils.error(t('invalid_image_format'));
                    return;
                  }
                  setUploadingStampImage(true);
                  try {
                    const cropped = await cropImageToCircle(file);
                    const data = await SitesService.uploadImage(cropped, 'campaigns/loyalty');
                    const s3Key = data?.s3Key || (data?.path ? `sites/images/${data.path}` : null);
                    if (s3Key) {
                      handleChange('completedStampImage', s3Key);
                      if (data?.url) setStampPreviewUrl(data.url);
                      SnackbarUtils.success(t('image_uploaded'));
                    } else {
                      SnackbarUtils.error(t('error_uploading_image'));
                    }
                  } catch (err) {
                    console.error('Error uploading stamp image:', err);
                    SnackbarUtils.error(err?.message || t('error_uploading_image'));
                  } finally {
                    setUploadingStampImage(false);
                    if (stampImageInputRef.current) stampImageInputRef.current.value = '';
                  }
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ImageIcon />}
                  onClick={() => stampImageInputRef.current?.click()}
                  disabled={uploadingStampImage}
                >
                  {uploadingStampImage ? t('uploading') : t('upload_image_circle')}
                </Button>
                {formData.completedStampImage && (
                  <Chip
                    label={t('image_configured')}
                    onDelete={() => { handleChange('completedStampImage', ''); setStampPreviewUrl(''); }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
              {stampPreviewUrl && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {t('loyalty_card_stamp_preview')}
                  </Typography>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: 'divider',
                      bgcolor: 'action.hover'
                    }}
                  >
                    <img
                      src={stampPreviewUrl}
                      alt={t('loyalty_card_stamp_preview')}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {t('loyalty_card_email_background_color') || 'Color de fondo'}
                  </Typography>
                  <input
                    type="color"
                    value={formData.emailBackgroundColor || '#ffffff'}
                    onChange={(e) => handleChange('emailBackgroundColor', e.target.value)}
                    style={{ width: 40, height: 36, padding: 2, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
                  />
                </Box>
                <TextField
                  size="small"
                  value={formData.emailBackgroundColor || '#ffffff'}
                  onChange={(e) => handleChange('emailBackgroundColor', e.target.value)}
                  placeholder="#ffffff"
                  sx={{ width: 110 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {t('loyalty_card_email_text_color') || 'Color del texto'}
                  </Typography>
                  <input
                    type="color"
                    value={formData.emailTextColor || '#333333'}
                    onChange={(e) => handleChange('emailTextColor', e.target.value)}
                    style={{ width: 40, height: 36, padding: 2, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
                  />
                </Box>
                <TextField
                  size="small"
                  value={formData.emailTextColor || '#333333'}
                  onChange={(e) => handleChange('emailTextColor', e.target.value)}
                  placeholder="#333333"
                  sx={{ width: 110 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Box>
            </Grid>

            {/* Sección: Tipo de recompensa */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                {t('reward_configuration') || '2. Configuración de la recompensa'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  {t('reward_type') || 'Tipo de recompensa'}
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.rewardType}
                  onChange={(e) => handleChange('rewardType', e.target.value)}
                >
                  <FormControlLabel 
                    value="free_item" 
                    control={<Radio />} 
                    label={t('free_item') || 'Artículo gratis'} 
                  />
                  <FormControlLabel 
                    value="percentage" 
                    control={<Radio />} 
                    label={t('percentage_discount') || 'Descuento por porcentaje'} 
                  />
                  <FormControlLabel 
                    value="amount" 
                    control={<Radio />} 
                    label={t('amount_discount') || 'Descuento por monto'} 
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Campos específicos según el tipo de recompensa */}
            {formData.rewardType === 'free_item' && (
              <>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label={t('free_item_description') || 'Descripción del artículo gratis'}
                    value={formData.freeItemDescription}
                    onChange={(e) => handleChange('freeItemDescription', e.target.value)}
                    placeholder={t('free_item_description_placeholder') || 'Ej: 1 helado gratis, 1 bebida gratis, etc.'}
                    helperText={t('free_item_description_helper') || 'Describe qué artículo recibirá el cliente gratis'}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('max_discount_amount') || 'Descuento máximo (monto)'}
                    value={formData.discountAmount || ''}
                    onChange={(e) => handleChange('discountAmount', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText={t('max_discount_amount_helper') || 'Ingresa el monto máximo del descuento que se aplicará al artículo gratis (opcional)'}
                  />
                </Grid>
              </>
            )}

            {formData.rewardType === 'percentage' && (
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('discount_percentage') || 'Porcentaje de descuento (%)'}
                  value={formData.discountPercentage}
                  onChange={(e) => handleChange('discountPercentage', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 1, max: 100, step: 1 }}
                  helperText={t('discount_percentage_helper') || 'Ingresa un valor entre 1 y 100'}
                />
              </Grid>
            )}

            {formData.rewardType === 'amount' && (
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('discount_amount') || 'Monto de descuento'}
                  value={formData.discountAmount}
                  onChange={(e) => handleChange('discountAmount', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={t('discount_amount_helper') || 'Ingresa el monto del descuento'}
                />
              </Grid>
            )}

            {/* Resumen de la campaña */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: '#E3F2FD', mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('campaign_summary') || 'Resumen de la campaña'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {formData.rewardType === 'free_item' 
                      ? (() => {
                          const baseText = t('loyalty_card_summary_free_item', { 
                            purchases: formData.purchasesRequired,
                            item: formData.freeItemDescription || t('free_item')
                          }) || `Por cada ${formData.purchasesRequired} compras, el cliente recibirá: ${formData.freeItemDescription || 'un artículo gratis'}`;
                          const discountText = formData.discountAmount > 0 
                            ? ` (descuento máximo: ${formatMoney(formData.discountAmount)})`
                            : '';
                          return baseText + discountText;
                        })()
                      : formData.rewardType === 'percentage'
                      ? t('loyalty_card_summary_percentage', { 
                          purchases: formData.purchasesRequired,
                          percentage: formData.discountPercentage
                        }) || `Por cada ${formData.purchasesRequired} compras, el cliente recibirá un descuento del ${formData.discountPercentage}%`
                      : t('loyalty_card_summary_amount', { 
                          purchases: formData.purchasesRequired,
                          amount: formData.discountAmount
                        }) || `Por cada ${formData.purchasesRequired} compras, el cliente recibirá un descuento de ${formatMoney(formData.discountAmount)}`
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                    {t('loyalty_card_info') || 'El sistema rastreará automáticamente las compras de cada cliente y aplicará la recompensa cuando se alcance el número de compras configurado.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Preview del correo */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
                {t('email_preview') || 'Vista previa del correo'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                  <Tab label={t('loyalty_card_preview') || 'Vista de cartilla'} />
                  <Tab label={t('email_preview') || 'Vista del correo'} />
                </Tabs>
              </Box>

              {activeTab === 0 && (() => {
                const headerColor = formData.emailHeaderColor || '#1976d2';
                const stampsColor = formData.emailStampsColor || '#4caf50';
                const backgroundColor = formData.emailBackgroundColor || '#ffffff';
                const textColor = formData.emailTextColor || '#333333';
                const filledCount = Math.floor(formData.purchasesRequired * 0.6);
                return (
                <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider', bgcolor: backgroundColor, color: textColor }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3, color: headerColor }}>
                    {t('loyalty_card_title') || 'Cartilla de Fidelidad'}
                  </Typography>
                  {(formData.startDate || formData.endDate) && (
                    <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: textColor, opacity: 0.8 }}>
                      {formData.startDate && formData.endDate
                        ? t('loyalty_card_valid_from_until', { from: formData.startDate, to: formData.endDate })
                        : formData.startDate
                          ? t('loyalty_card_valid_from', { from: formData.startDate })
                          : t('loyalty_card_valid_until', { to: formData.endDate })}
                    </Typography>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 60px)', justifyContent: 'center', gap: 2, mb: 3 }}>
                    {Array.from({ length: formData.purchasesRequired }).map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 60,
                          height: 60,
                          border: '2px solid',
                          borderColor: index < filledCount ? stampsColor : 'grey.300',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: index < filledCount ? stampsColor : 'white',
                          position: 'relative'
                        }}
                      >
                        {index < filledCount && (
                          stampPreviewUrl ? (
                            <Box
                              component="img"
                              src={stampPreviewUrl}
                              alt=""
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }}
                            />
                          ) : (
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                              ✓
                            </Typography>
                          )
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            position: 'absolute',
                            bottom: -20,
                            fontSize: '0.7rem',
                            color: textColor,
                            opacity: 0.8
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: textColor, opacity: 0.8 }}>
                      {t('progress_info', { 
                        current: filledCount,
                        total: formData.purchasesRequired
                      }) || `${filledCount} de ${formData.purchasesRequired} compras completadas`}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: headerColor }}>
                      {t('your_reward') || 'Tu recompensa'}
                    </Typography>
                    <Card variant="outlined" sx={{ bgcolor: headerColor, color: 'white', mt: 2, p: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formData.rewardType === 'free_item' 
                          ? (() => {
                              const baseText = formData.freeItemDescription || t('free_item');
                              const discountText = formData.discountAmount > 0 
                                ? ` (descuento máximo: ${formatMoney(formData.discountAmount)})`
                                : '';
                              return baseText + discountText;
                            })()
                          : formData.rewardType === 'percentage'
                          ? `${formData.discountPercentage}% ${t('discount')}`
                          : `${formatMoney(formData.discountAmount)} ${t('discount')}`
                        }
                      </Typography>
                    </Card>
                  </Box>
                </Paper>
                );
              })()}

              {activeTab === 1 && (() => {
                const headerColor = formData.emailHeaderColor || '#1976d2';
                const stampsColor = formData.emailStampsColor || '#4caf50';
                const backgroundColor = formData.emailBackgroundColor || '#ffffff';
                const textColor = formData.emailTextColor || '#333333';
                const filledCount = Math.floor(formData.purchasesRequired * 0.6);
                return (
                <Paper sx={{ p: 0, border: '1px solid', borderColor: 'divider', minHeight: 400, overflow: 'hidden', bgcolor: backgroundColor }}>
                  <Box sx={{ py: 2, px: 2, bgcolor: headerColor, color: 'white', textAlign: 'center' }}>
                    <Typography variant="h6">
                      {t('loyalty_card_email_subject') || 'Tu Cartilla de Fidelidad'}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, fontFamily: 'Arial, sans-serif', color: textColor, bgcolor: backgroundColor }}>
                    <Typography variant="body1" paragraph>
                      {t('loyalty_card_email_greeting') || '¡Hola!'}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {t('loyalty_card_email_intro') || 'Te informamos sobre el progreso de tu cartilla de fidelidad:'}
                    </Typography>
                    {(formData.startDate || formData.endDate) && (
                      <Typography variant="body2" paragraph sx={{ color: textColor, opacity: 0.8 }}>
                        {formData.startDate && formData.endDate
                          ? t('loyalty_card_valid_from_until', { from: formData.startDate, to: formData.endDate })
                          : formData.startDate
                            ? t('loyalty_card_valid_from', { from: formData.startDate })
                            : t('loyalty_card_valid_until', { to: formData.endDate })}
                      </Typography>
                    )}
                    <Box sx={{ my: 3, p: 2, bgcolor: backgroundColor, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: headerColor }}>
                        {t('your_progress') || 'Tu progreso'}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 40px)', justifyContent: 'center', gap: 1.5, mb: 2 }}>
                        {Array.from({ length: formData.purchasesRequired }).map((_, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: 40,
                              height: 40,
                              border: '2px solid',
                              borderColor: index < filledCount ? stampsColor : 'grey.300',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: index < filledCount ? stampsColor : 'white'
                            }}
                          >
                            {index < filledCount && (
                              stampPreviewUrl ? (
                                <Box
                                  component="img"
                                  src={stampPreviewUrl}
                                  alt=""
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }}
                                />
                              ) : (
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                                  ✓
                                </Typography>
                              )
                            )}
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="body2" sx={{ textAlign: 'center', color: textColor, opacity: 0.8 }}>
                        {t('progress_info', { 
                          current: filledCount,
                          total: formData.purchasesRequired
                        }) || `${filledCount} de ${formData.purchasesRequired} compras completadas`}
                      </Typography>
                    </Box>

                    <Typography variant="body1" paragraph sx={{ color: textColor }}>
                      {t('loyalty_card_email_reward_info', {
                        purchases: formData.purchasesRequired - filledCount,
                        reward: formData.rewardType === 'free_item' 
                          ? (() => {
                              const baseText = formData.freeItemDescription || t('free_item');
                              const discountText = formData.discountAmount > 0 
                                ? ` (descuento máximo: ${formatMoney(formData.discountAmount)})`
                                : '';
                              return baseText + discountText;
                            })()
                          : formData.rewardType === 'percentage'
                          ? `${formData.discountPercentage}% ${t('discount')}`
                          : `${formatMoney(formData.discountAmount)} ${t('discount')}`
                      }) || (() => {
                        const rewardText = formData.rewardType === 'free_item' 
                          ? (() => {
                              const baseText = formData.freeItemDescription || 'un artículo gratis';
                              const discountText = formData.discountAmount > 0 
                                ? ` (descuento máximo: ${formatMoney(formData.discountAmount)})`
                                : '';
                              return baseText + discountText;
                            })()
                          : formData.rewardType === 'percentage'
                          ? `${formData.discountPercentage}% de descuento`
                          : `${formatMoney(formData.discountAmount)} de descuento`;
                        return `Te faltan ${formData.purchasesRequired - filledCount} compras para obtener tu recompensa: ${rewardText}`;
                      })()}
                    </Typography>
                  </Box>
                </Paper>
                );
              })()}
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              startIcon={getIcon('save')}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving 
                ? (t('saving') || 'Guardando...') 
                : (t('save') || 'Guardar')
              }
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LoyaltyCardTab;
